import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  type BillingCycle,
  AddonConfigSchema,
  BillingSettingsSchema,
  InvoiceSchema,
  MarketConfigSchema,
  PspConfigSchema,
  PaymentMethodSchema,
  PaymentSchema,
  PlanPriceSchema,
  RefundSchema,
  RegionsConfigSchema,
  type RegionsConfig,
  SubscriptionSchema,
  effectiveCommercialMode,
  TenantSchema,
  UsageCountersSchema,
  isTestModeRecord,
  type AddonConfig,
  type BillingSettings,
  type Invoice,
  type MarketConfig,
  type PspConfig,
  type Payment,
  type PaymentMethod,
  type PaymentMethodType,
  type PaymentProvider,
  type PlanPrice,
  type PaymentStatus,
  type Refund,
  type RefundReason,
  type RefundStatus,
  type Subscription,
  type Tenant,
  type UsageCounters,
} from '@etiquetachef/types';
import type { DocResult, UsageDocResult } from '@/lib/firestoreTypes';
import { toEmailLogEntry, type EmailLogEntry } from './emailLogs';
import { reportNonFatal, serializeError } from './errorReport';
import { firestore } from './firebase';
import { snapshotData } from './firestoreNormalize';
import { normalizeLocale } from './firestoreTenants';

const TAG = 'admin/tenantDetail';

/**
 * Estrutura mínima de um schema Zod — evita importar `zod` direto no admin
 * só pra tipar o helper (não é dep declarada aqui).
 */
type SafeParser<T> = {
  safeParse(raw: unknown):
    | { success: true; data: T }
    | { success: false; error: { issues: unknown[] } };
};

/**
 * Faz `safeParse` e, em caso de falha, LOGA os `issues` do Zod antes de
 * devolver `null`. Sem isso o doc fora do schema vira um erro opaco ("Falha
 * ao ler assinatura") sem nenhuma pista de qual campo divergiu — sem
 * DevTools do cliente, esse log é a única visibilidade (CLAUDE.md: nunca
 * silenciar). Ver `feedback_never_silence_logs`.
 *
 * Origem: sem issue — commit a32e5b94c, 2026-06-09 (envelope REQUIRED da
 * subscription criada via Checkout).
 *
 * Usado por: `subscribeTenant`, `subscribeBilling`, `getTenantSubscriptionOnce`,
 * `subscribeUsage` e `subscribeInvoice` deste módulo.
 *
 * Dicas: "nunca silenciar" aqui é só console — não há `reportNonFatal`; os
 * chamadores que viram `ERROR` mostram o alerta na tela, os demais não (#926).
 */
function parseOrWarn<T>(schema: SafeParser<T>, raw: unknown, label: string): T | null {
  const parsed = schema.safeParse(raw);
  if (parsed.success) return parsed.data;
  console.warn(`${TAG}: ${label} — doc fora do schema`, parsed.error.issues);
  return null;
}

export type { DocResult, UsageDocResult };
/** `DocResult` de `tenants/{tid}/billing/subscription`. */
export type BillingDocResult = DocResult<Subscription>;

/**
 * Listener genérico de UM documento: `NOT_FOUND`, `READY` ou `ERROR` (parse
 * falhou ou erro de snapshot).
 *
 * Origem: sem issue — commit c5a7bd86a, 2026-04-26 (detalhe de tenant, Sprint 2).
 *
 * Contrato: o `parser` recebe `{ id, ...snap.data() }` CRU — sem
 * `snapshotData`, então Timestamp chega como objeto e o schema tem de
 * aceitá-lo (o do tenant não aceita, por isso `subscribeTenant` não usa este).
 *
 * Usado por: `subscribeBilling`, `subscribeUsage`, `subscribeInvoice` e
 * `subscribeBillingSettings` deste módulo.
 */
function subscribeDoc<T>(
  path: readonly [string, ...string[]],
  parser: (raw: unknown) => T | null,
  onResult: (r: DocResult<T>) => void,
): Unsubscribe {
  const [first, ...rest] = path;
  const ref = doc(firestore, first, ...rest);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onResult({ kind: 'NOT_FOUND' });
        return;
      }
      const parsed = parser({ id: snap.id, ...snap.data() });
      if (!parsed) {
        onResult({ kind: 'ERROR', error: new Error(`zod parse failed for ${snap.ref.path}`) });
        return;
      }
      onResult({ kind: 'READY', data: parsed });
    },
    (err) => {
      console.error(`${TAG}: snapshot error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

/** `DocResult` do doc raiz `tenants/{tid}`. */
export type TenantDocResult = DocResult<Tenant>;

/**
 * Assina o doc raiz do tenant para o detalhe (`/tenants/:tenantId`).
 *
 * Origem: sem issue — commit c5a7bd86a, 2026-04-26 (detalhe de tenant, Sprint 2).
 *
 * Contrato: `NOT_FOUND` se o doc não existe; `READY` com o tenant tolerado
 * (Timestamp → ms, `active` → `status`, `locale` legado); `ERROR` se o Zod
 * recusar ou o snapshot falhar.
 *
 * Usado por: `features/tenants/useTenantDetail.ts` (tela
 * `routes/TenantDetailPage.tsx`, spec `docs/screens/tenant-detail-admin.md`).
 *
 * Arquitetura: rule `match /tenants/{tenantId}` (super-admin lê).
 *
 * Dados: `tenants/{tenantId}` — `TenantSchema`.
 *
 * Falhas conhecidas:
 *  - Locale legado (`pt`) derrubava o parse e o `TenantInfoCard` ficava em
 *    loading infinito — por isso o mesmo tratamento da listagem (ver o
 *    comentário no corpo).
 */
export function subscribeTenant(
  tenantId: string,
  onResult: (r: TenantDocResult) => void,
): Unsubscribe {
  console.info(`${TAG}: subscribe tenant=${tenantId}`);
  // NÃO usa o `subscribeDoc` genérico: o doc do tenant precisa do MESMO
  // tratamento tolerante da listagem (`firestoreTenants.normalizeTenantData`)
  // antes do parse — senão tenants legados travam o detalhe. Dois pontos:
  //   1. `snapshotData` converte Timestamp → millis recursivamente (campos de
  //      tempo legados vêm como `Timestamp`, não number).
  //   2. `normalizeLocale` coage locale BCP-47 livre (`pt`, `en-US`, …) para o
  //      enum canônico (B-21). Sem isto, `locale: "pt"` derruba o `safeParse`
  //      e o `TenantInfoCard` fica em loading infinito (tenant.kind=ERROR →
  //      `tenantData` null → `<LoadingBox/>` eterno).
  // Espelha `parseSnapshot` da listagem para comportamento idêntico nas duas
  // telas.
  const ref = doc(firestore, 'tenants', tenantId);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onResult({ kind: 'NOT_FOUND' });
        return;
      }
      const raw: Record<string, unknown> = { ...snapshotData(snap), id: snap.id };
      if (raw.status === undefined && raw.active === true) raw.status = 'ACTIVE';
      raw.locale = normalizeLocale(raw.locale);
      const parsed = parseOrWarn(TenantSchema, raw, `tenant ${tenantId}`);
      if (!parsed) {
        onResult({
          kind: 'ERROR',
          error: new Error(`zod parse failed for ${snap.ref.path}`),
        });
        return;
      }
      onResult({ kind: 'READY', data: parsed });
    },
    (err) => {
      console.error(`${TAG}: tenant snapshot error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

/**
 * Assina a assinatura do tenant (`tenants/{tid}/billing/subscription`).
 *
 * Origem: sem issue — commit c5a7bd86a, 2026-04-26 (Sprint 2).
 *
 * Usado por: `features/tenants/useTenantDetail.ts` e
 * `routes/BillingOverviewPage.tsx`.
 *
 * Arquitetura: fonte da verdade do plano efetivo e do preço congelado do
 * tenant (CLAUDE.md, "Fonte da verdade"). Escrita só pelas Functions.
 *
 * Dados: `tenants/{tid}/billing/subscription` — `SubscriptionSchema`
 * (`plan_id`, `amount`/`currency`/`billing_cycle`, `commercial_mode`,
 * `courtesy`, `items[]`, `total_amount_cents` em CENTAVOS, …).
 */
export function subscribeBilling(
  tenantId: string,
  onResult: (r: BillingDocResult) => void,
): Unsubscribe {
  return subscribeDoc(
    ['tenants', tenantId, 'billing', 'subscription'],
    (raw) => parseOrWarn(SubscriptionSchema, raw, `subscription ${tenantId}`),
    onResult,
  );
}

/**
 * Get one-shot da subscription de um tenant (mesma semântica de
 * `subscribeBilling`: NOT_FOUND/READY/ERROR). Usado pela lista de empresas
 * para derivar o status comercial por linha sem manter N listeners abertos —
 * a lista é snapshot (callable `searchTenants`), então re-busca no refresh/
 * troca de página em vez de tempo-real.
 *
 * Origem: #100 — commit d83f9a59b, 2026-06-28 (coluna Assinatura na lista).
 *
 * Contrato: nunca rejeita — erro de leitura vira `ERROR`.
 *
 * Usado por: `features/tenants/useTenantBillingStatuses.ts`.
 */
export async function getTenantSubscriptionOnce(tenantId: string): Promise<BillingDocResult> {
  try {
    const ref = doc(firestore, 'tenants', tenantId, 'billing', 'subscription');
    const snap = await getDoc(ref);
    if (!snap.exists()) return { kind: 'NOT_FOUND' };
    const parsed = parseOrWarn(
      SubscriptionSchema,
      { id: snap.id, ...snap.data() },
      `subscription ${tenantId}`,
    );
    if (!parsed) return { kind: 'ERROR', error: new Error(`zod parse failed for ${ref.path}`) };
    return { kind: 'READY', data: parsed };
  } catch (err) {
    console.error(`${TAG}: getTenantSubscriptionOnce error tenant=${tenantId}`, err);
    // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
    reportNonFatal('admin_firestore_tenant_detail_failed', {
      site: 'getTenantSubscriptionOnce error tenant=',
      code: (err as { code?: string }).code ?? null,
      ...serializeError(err),
    });
    return { kind: 'ERROR', error: err };
  }
}

/**
 * Assina o contador de uso de um mês (`yyyymm`) do tenant.
 *
 * Origem: sem issue — commit c5a7bd86a, 2026-04-26 (Sprint 2).
 *
 * Usado por: `features/tenants/useTenantDetail.ts`.
 *
 * Dados: `tenants/{tid}/billing/usage/months/{yyyymm}` — `UsageCountersSchema`;
 * escrito pelos triggers de impressão no servidor.
 */
export function subscribeUsage(
  tenantId: string,
  yyyymm: string,
  onResult: (r: UsageDocResult) => void,
): Unsubscribe {
  return subscribeDoc(
    ['tenants', tenantId, 'billing', 'usage', 'months', yyyymm],
    (raw) => parseOrWarn(UsageCountersSchema, raw, `usage ${tenantId}/${yyyymm}`),
    onResult,
  );
}

/** Estado do histórico de uso (até `USAGE_HISTORY_LIMIT` meses). */
export type UsageHistoryResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; entries: UsageCounters[] }
  | { kind: 'ERROR'; error: unknown };

/** Meses mostrados no histórico de uso (um ano). */
export const USAGE_HISTORY_LIMIT = 12;

/**
 * Assina os meses de uso do tenant, mais recentes primeiro.
 *
 * Origem: sem issue — commit 3a98de728, 2026-04-26 (aba Uso com histórico mensal).
 *
 * Contrato: lê a coleção INTEIRA e ordena/corta no cliente (ver comentário
 * no corpo — `orderBy(documentId(), 'desc')` exigiria índice).
 *
 * Usado por: `features/usage/useUsageHistory.ts`.
 *
 * Dados: `tenants/{tid}/billing/usage/months/{yyyymm}` — `UsageCountersSchema`.
 *
 * Falhas conhecidas:
 *  - Doc fora do schema some da lista só com `console.warn`, sem report na
 *    telemetria (#926).
 */
export function subscribeUsageHistory(
  tenantId: string,
  onResult: (r: UsageHistoryResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'tenants', tenantId, 'billing', 'usage', 'months');
  // IDs são `yyyymm` (lexicograficamente = cronologicamente ordenáveis).
  // Evitamos `orderBy(documentId(), 'desc')`: Firestore exige índice composto
  // para ordenação DESC por __name__ (FAILED_PRECONDITION → "Falha ao ler
  // histórico de uso"). A coleção é pequena (~1 doc/mês), então ordenamos e
  // cortamos os mais recentes no cliente.
  return onSnapshot(
    ref,
    (snap) => {
      const recent = [...snap.docs]
        .sort((a, b) => b.id.localeCompare(a.id))
        .slice(0, USAGE_HISTORY_LIMIT);
      const entries: UsageCounters[] = [];
      for (const d of recent) {
        const parsed = UsageCountersSchema.safeParse(d.data());
        if (parsed.success) {
          entries.push(parsed.data);
        } else {
          console.warn(`${TAG}: usage doc ${d.id} ignorado`, parsed.error.issues);
        }
      }
      onResult({ kind: 'READY', entries });
    },
    (err) => {
      console.error(`${TAG}: usage history error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

// ---------- Sprint 16 — Invoices, Payments, PaymentMethods, Settings, PlanPrices ----------

/** Estado de uma lista de faturas. */
export type InvoiceListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; invoices: Invoice[] }
  | { kind: 'ERROR'; error: unknown };

/** Teto das listas de faturas e pagamentos por tenant (sem paginação). */
export const INVOICES_LIST_LIMIT = 100;

/**
 * Assina as faturas do tenant, `issued_at desc`, até `INVOICES_LIST_LIMIT`.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 (rotas /billing/*, Sprint 16).
 *
 * Usado por: `routes/InvoicesPage.tsx`, `routes/BillingOverviewPage.tsx`,
 * `components/InvoiceTable.tsx`, `features/finance/TenantFinanceTab.tsx`.
 *
 * Arquitetura: rule do `match /tenants/{tenantId}` (faturas); escrita só
 * pelas Functions/webhooks de gateway. `orderBy` de campo único.
 *
 * Dados: `tenants/{tid}/invoices/{id}` — `InvoiceSchema` (valores em centavos).
 *
 * Falhas conhecidas:
 *  - Doc fora do schema some da lista só com `console.warn`, sem report na
 *    telemetria (#926).
 */
export function subscribeTenantInvoices(
  tenantId: string,
  onResult: (r: InvoiceListResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'tenants', tenantId, 'invoices');
  const q = query(ref, orderBy('issued_at', 'desc'), fsLimit(INVOICES_LIST_LIMIT));
  return onSnapshot(
    q,
    (snap) => {
      const invoices: Invoice[] = [];
      for (const d of snap.docs) {
        const parsed = InvoiceSchema.safeParse({ id: d.id, ...d.data() });
        if (parsed.success) {
          invoices.push(parsed.data);
        } else {
          console.warn(`${TAG}: invoice doc ${d.id} ignorado`, parsed.error.issues);
        }
      }
      onResult({ kind: 'READY', invoices });
    },
    (err) => {
      console.error(`${TAG}: invoices error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

/**
 * Assina uma fatura pelo id (detalhe da fatura).
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 (Sprint 16).
 *
 * Usado por: `routes/InvoiceDetailPage.tsx`.
 */
export function subscribeInvoice(
  tenantId: string,
  invoiceId: string,
  onResult: (r: DocResult<Invoice>) => void,
): Unsubscribe {
  return subscribeDoc(
    ['tenants', tenantId, 'invoices', invoiceId],
    (raw) => parseOrWarn(InvoiceSchema, raw, `invoice ${tenantId}/${invoiceId}`),
    onResult,
  );
}

/** Estado de uma lista de pagamentos. */
export type PaymentListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; payments: Payment[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Assina os pagamentos do tenant, `created_at desc`, até `INVOICES_LIST_LIMIT`.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 (Sprint 16).
 *
 * Usado por: `routes/PaymentsPage.tsx`, `features/billing/BillingCard.tsx`,
 * `features/finance/RequestRefundDialog.tsx`, `features/finance/TenantFinanceTab.tsx`.
 *
 * Dados: `tenants/{tid}/payments/{id}` — `PaymentSchema`.
 *
 * Falhas conhecidas:
 *  - Doc fora do schema some da lista só com `console.warn`, sem report na
 *    telemetria (#926).
 */
export function subscribeTenantPayments(
  tenantId: string,
  onResult: (r: PaymentListResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'tenants', tenantId, 'payments');
  const q = query(ref, orderBy('created_at', 'desc'), fsLimit(INVOICES_LIST_LIMIT));
  return onSnapshot(
    q,
    (snap) => {
      const payments: Payment[] = [];
      for (const d of snap.docs) {
        const parsed = PaymentSchema.safeParse({ id: d.id, ...d.data() });
        if (parsed.success) {
          payments.push(parsed.data);
        } else {
          console.warn(`${TAG}: payment doc ${d.id} ignorado`, parsed.error.issues);
        }
      }
      onResult({ kind: 'READY', payments });
    },
    (err) => {
      console.error(`${TAG}: payments error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

/**
 * Assina os pagamentos de UMA fatura do tenant.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 (Sprint 16).
 *
 * Usado por: `routes/InvoiceDetailPage.tsx`.
 *
 * Arquitetura: índice composto `payments (invoice_id ASC, created_at DESC)`,
 * escopo COLLECTION, em `firebase/firestore.indexes.json`.
 *
 * Falhas conhecidas:
 *  - Pagamento fora do schema é descartado sem log nem report (#926).
 */
export function subscribePaymentsForInvoice(
  tenantId: string,
  invoiceId: string,
  onResult: (r: PaymentListResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'tenants', tenantId, 'payments');
  const q = query(ref, where('invoice_id', '==', invoiceId), orderBy('created_at', 'desc'));
  return onSnapshot(
    q,
    (snap) => {
      const payments: Payment[] = [];
      for (const d of snap.docs) {
        const parsed = PaymentSchema.safeParse({ id: d.id, ...d.data() });
        if (parsed.success) payments.push(parsed.data);
      }
      onResult({ kind: 'READY', payments });
    },
    (err) => {
      console.error(`${TAG}: payments-for-invoice error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

/** Estado da lista de meios de pagamento. */
export type PaymentMethodListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; methods: PaymentMethod[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Assina os meios de pagamento salvos do tenant.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 (Sprint 16).
 *
 * Usado por: `routes/PaymentMethodsPage.tsx`.
 *
 * Falhas conhecidas:
 *  - Doc fora do schema é descartado sem log nem report (#926).
 */
export function subscribePaymentMethods(
  tenantId: string,
  onResult: (r: PaymentMethodListResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'tenants', tenantId, 'paymentMethods');
  return onSnapshot(
    ref,
    (snap) => {
      const methods: PaymentMethod[] = [];
      for (const d of snap.docs) {
        const parsed = PaymentMethodSchema.safeParse({ id: d.id, ...d.data() });
        if (parsed.success) methods.push(parsed.data);
      }
      onResult({ kind: 'READY', methods });
    },
    (err) => {
      console.error(`${TAG}: paymentMethods error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

/**
 * Assina as configurações globais de cobrança (`config/billing/settings/singleton`).
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 (Sprint 16).
 *
 * Usado por: `routes/BillingEmailsPage.tsx`.
 *
 * Arquitetura: rule `match /config/billing/settings/{docId}`.
 */
export function subscribeBillingSettings(
  onResult: (r: DocResult<BillingSettings>) => void,
): Unsubscribe {
  return subscribeDoc(
    ['config', 'billing', 'settings', 'singleton'],
    (raw) => {
      const parsed = BillingSettingsSchema.safeParse(raw);
      return parsed.success ? parsed.data : null;
    },
    onResult,
  );
}

/** Estado da lista de preços de plano. */
export type PlanPriceListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; prices: PlanPrice[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Assina `config/planPrices/items` (preços de catálogo).
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 (Sprint 16).
 *
 * Usado por: nenhum consumidor no `apps/admin` em 2026-10-10 (export sem
 * uso, achado da #926).
 */
export function subscribePlanPrices(
  onResult: (r: PlanPriceListResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'config', 'planPrices', 'items');
  return onSnapshot(
    ref,
    (snap) => {
      const prices: PlanPrice[] = [];
      for (const d of snap.docs) {
        const parsed = PlanPriceSchema.safeParse({ id: d.id, ...d.data() });
        if (parsed.success) prices.push(parsed.data);
        else console.warn(`${TAG}: planPrice doc ${d.id} ignorado`, parsed.error.issues);
      }
      onResult({ kind: 'READY', prices });
    },
    (err) => {
      console.error(`${TAG}: planPrices error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

// ---------- Sprint 35 PR G5 — markets ----------

/** Estado da lista de mercados (países). */
export type MarketConfigListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; markets: MarketConfig[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Lê todos os docs de `/config/markets/items/`. Sem ordenação no
 * Firestore — caller (`MarketsPage`) ordena pelo enum `Country` pra
 * priorizar países saleable primeiro, UE bloqueada depois.
 *
 * Origem: sem issue — commit 5af121d4c, 2026-05-25 (Sprint 35, markets CRUD).
 *
 * Usado por: `routes/MarketsPage.tsx`, `routes/ModuleCatalogImportPage.tsx`,
 * `features/sellerEntities/SellerEntitiesPage.tsx`,
 * `features/countries/CountriesTab.tsx`.
 *
 * Arquitetura: rule `match /config/markets/items/{country}`.
 *
 * Dados: `config/markets/items/{country}` — `MarketConfigSchema` (moeda,
 * PSP do país, entidade vendedora, venda liberada).
 *
 * Falhas conhecidas:
 *  - Doc fora do schema some da lista só com `console.warn`, sem report na
 *    telemetria (#926).
 */
export function subscribeMarketConfigs(
  onResult: (r: MarketConfigListResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'config', 'markets', 'items');
  return onSnapshot(
    ref,
    (snap) => {
      const markets: MarketConfig[] = [];
      for (const d of snap.docs) {
        const parsed = MarketConfigSchema.safeParse(d.data());
        if (parsed.success) {
          markets.push(parsed.data);
          // #1078 (auditoria F2): `region` desconhecida vira ausente no schema (`.catch`) — a linha
          // mostra o padrão do grupo, mas a criação de tenant do país está BLOQUEADA no servidor
          // até alguém escolher uma região válida (ou "Padrão do grupo") no editor.
          const rawRegion: unknown = d.get('region');
          if (rawRegion !== undefined && rawRegion !== null && parsed.data.region === undefined) {
            reportNonFatal('admin_market_region_invalid', { country: d.id });
          }
        } else
          console.warn(
            `${TAG}: marketConfig doc ${d.id} ignorado`,
            parsed.error.issues,
          );
      }
      onResult({ kind: 'READY', markets });
    },
    (err) => {
      console.error(`${TAG}: markets error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

// ---------- #1078 (F2 da #960) — chave das regiões (/config/regions) ----------

/** Estado da chave das regiões. `config: null` = doc ausente ou inválido (= desligada). */
export type RegionsConfigResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; config: RegionsConfig | null }
  | { kind: 'ERROR'; error: unknown };

/**
 * Assina `config/regions` — a chave que põe em vigor o mapa país → região dos tenants novos.
 *
 * Origem: #1078 — 2026-10-29 10:20 +0100 (F2 da #960).
 *
 * Contrato: doc ausente → `READY` com `config: null` (desligada, como o servidor lê); doc fora do
 * `RegionsConfigSchema` → também `null`, com report `admin_regions_config_invalid` (o servidor
 * faz o mesmo em `apps/functions/src/lib/regionsConfig.ts`: inválido = desligado + report).
 *
 * Usado por: `features/countries/RegionsSwitch.tsx` (aba Países de `/countries`).
 *
 * Arquitetura: rule `match /config/regions` — só super-admin lê; ninguém escreve pelo cliente
 * (callable `setRegionsConfig`).
 *
 * Dados: `{ "europeWest1Enabled": false, "updated_at": 1800000000000, "updated_by": "<uid>" }`.
 */
export function subscribeRegionsConfig(onResult: (r: RegionsConfigResult) => void): Unsubscribe {
  return onSnapshot(
    doc(firestore, 'config', 'regions'),
    (snap) => {
      if (!snap.exists()) {
        onResult({ kind: 'READY', config: null });
        return;
      }
      const parsed = RegionsConfigSchema.safeParse(snap.data());
      if (!parsed.success) {
        console.warn(`${TAG}: config/regions inválido`, parsed.error.issues);
        reportNonFatal('admin_regions_config_invalid', { issues: parsed.error.issues.length });
      }
      onResult({ kind: 'READY', config: parsed.success ? parsed.data : null });
    },
    (err) => {
      console.error(`${TAG}: regions error`, err);
      // Auditoria 2026-10-29 10:57 +0100 (#1078): a falha de leitura só ia para o console — a chave
      // aparecia "não foi possível carregar" sem nenhum evento no PostHog (sem DevTools do cliente).
      // Mesmo padrão de #1021 em `getTenantSubscriptionOnce`.
      reportNonFatal('admin_regions_config_failed', {
        code: (err as { code?: string }).code ?? null,
        ...serializeError(err),
      });
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

// ---------- Sprint 40 — catálogo aberto de PSP (/config/psps) ----------

/** Estado do catálogo de PSPs. */
export type PspConfigListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; psps: PspConfig[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Lê todos os docs de `/config/psps/items/`. Sem ordenação no Firestore —
 * caller ordena (ex.: por display_name). Usado pela aba PSP de `/countries` e
 * pelo dropdown de PSP da aba Países (filtra `enabled`).
 *
 * Origem: sem issue — commit 2b485856b, 2026-06-11 (Sprint 36, aba PSP).
 *
 * Usado por: `features/psp/PspListPage.tsx`, `features/psp/PspEditorPage.tsx`
 * e `features/billing/MarketPspFields.tsx`.
 *
 * Arquitetura: rule `match /config/psps/items/{pspId}`.
 *
 * Dados: `config/psps/items/{pspId}` — `PspConfigSchema`.
 *
 * Falhas conhecidas:
 *  - Doc fora do schema some da lista só com `console.warn`, sem report na
 *    telemetria (#926).
 */
export function subscribePspConfigs(
  onResult: (r: PspConfigListResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'config', 'psps', 'items');
  return onSnapshot(
    ref,
    (snap) => {
      const psps: PspConfig[] = [];
      for (const d of snap.docs) {
        const parsed = PspConfigSchema.safeParse(d.data());
        if (parsed.success) psps.push(parsed.data);
        else console.warn(`${TAG}: pspConfig doc ${d.id} ignorado`, parsed.error.issues);
      }
      onResult({ kind: 'READY', psps });
    },
    (err) => {
      console.error(`${TAG}: psps error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

// ---------- Sprint 35 PR G1 — addons (/billing/addons) ----------

/** Estado do catálogo de add-ons. */
export type AddonConfigListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; addons: AddonConfig[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Lê todos os docs de `/config/addons/items/` em tempo real.
 *
 * Origem: Sprint 35 PR G1 — commit 1bc01f1b9, 2026-05-25 23:58 +0200 (sem
 * issue).
 *
 * Contrato: entrega `READY` com todos os add-ons válidos, ordenados no cliente
 * por `created_at desc`. A lista é pequena, então não há `orderBy` nem índice.
 * Add-ons com venda pausada (`disabled_at != null`, #679 D20) também vêm. Doc
 * que falha no `AddonConfigSchema` é descartado e reportado. Erro do listener
 * vira `ERROR` e também é reportado.
 *
 * Usado por: `AddonsPage` (`/billing/addons`), `ModuleCatalogImportPage`
 * (`/billing/addons/import`) e `BillingAddonsCard` (aba Cobrança do detalhe
 * do tenant), todos no admin.
 *
 * Dados: `/config/addons/items/{sku}`, schema `AddonConfigSchema` em
 * `@etiquetachef/types`. Quem escreve são os callables
 * `createAddon`/`updateAddon`/`disableAddon`/`enableAddon`.
 *
 * Falhas conhecidas: até 2026-10-09 (#907, auditoria) o doc inválido só ia
 * para `console.warn`, e o erro só para `console.error`. Em produção o módulo
 * sumia da tabela sem rastro, porque não temos DevTools do admin.
 *
 * Eventos: `admin_addon_config_dropped` (uma vez por montagem, com contagem
 * e amostra de SKUs) e `admin_addon_configs_failed`.
 */
export function subscribeAddonConfigs(
  onResult: (r: AddonConfigListResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'config', 'addons', 'items');
  // Uma vez por montagem: o listener reentrega o snapshot a cada mudança, e
  // reportar a cada entrega inundaria o PostHog com o mesmo doc ruim.
  let dropsReported = false;
  return onSnapshot(
    ref,
    (snap) => {
      const addons: AddonConfig[] = [];
      const dropped: string[] = [];
      for (const d of snap.docs) {
        const parsed = AddonConfigSchema.safeParse(d.data());
        if (parsed.success) addons.push(parsed.data);
        else {
          dropped.push(d.id);
          console.warn(`${TAG}: addonConfig doc ${d.id} ignorado`, parsed.error.issues);
        }
      }
      // Auditoria 2026-10-09 16:20 +0200 (#907): antes só havia console.warn;
      // o módulo sumia da tabela de add-ons sem nenhum sinal em produção.
      if (dropped.length > 0 && !dropsReported) {
        dropsReported = true;
        reportNonFatal('admin_addon_config_dropped', {
          count: dropped.length,
          sample: dropped.slice(0, 5),
        });
      }
      addons.sort((a, b) => b.created_at - a.created_at);
      onResult({ kind: 'READY', addons });
    },
    (err) => {
      console.error(`${TAG}: addons error`, err);
      reportNonFatal('admin_addon_configs_failed', serializeError(err));
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

// ---------- Isenções (/billing/promotions — só Lifetime Free desde a #679 F4) ----------

/** Linha da lista de cortesias (Lifetime Free). */
export type LifetimeFreeTenantRow = {
  tenantId: string;
  category: string;
  reason: string | null;
  appliedAt: number;
  appliedBy: string | null;
  /** `subscription.total_amount_cents` no momento da leitura — usado pelo
   *  card finance pra estimar receita "perdida" (sem o desconto, este
   *  é o valor que o tenant pagaria por ciclo). Quando ausente (subs
   *  antigas pré-Sprint 35 que ainda não migraram items[]), conta 0. */
  totalAmountCents: number;
  currency: string;
  billingCycle: BillingCycle;
  /** `subscription.livemode` (`null` = ausente/legado = live). O card do
   *  `/dashboard/finance` o usa para tirar o tenant sandbox do relatório
   *  (#1008); a lista de promoções (operacional) mostra todos. */
  livemode: boolean | null;
};

/** Estado da lista de cortesias. */
export type LifetimeFreeListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; rows: LifetimeFreeTenantRow[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Linha da lista a partir do doc, ou `null` se ele não é cortesia (#679 F7).
 * Lê o tipo **efetivo**: `courtesy{}` quando gravado, senão o cupom legado.
 * Demo não entra — deixou de ser "motivo de cortesia" para ser tipo próprio.
 *
 * Origem: #738 — commit aa66c0622, 2026-09-25 (cortesia e demo sem Stripe).
 *
 * Usado por: `subscribeLifetimeFreeTenants` (aqui); exportada para teste.
 *
 * Histórico:
 *  - 2026-10-19 20:57 +0200 — (#1008) linha carrega `livemode`, para o card do
 *    painel financeiro tirar o tenant sandbox.
 */
export function lifetimeFreeRowFrom(data: unknown): LifetimeFreeTenantRow | null {
  const parsed = SubscriptionSchema.safeParse(data);
  if (!parsed.success) return null;
  const sub = parsed.data;
  if (effectiveCommercialMode(sub) !== 'COURTESY') return null;
  const courtesy = sub.courtesy ?? null;
  const discount = sub.discount ?? null;
  return {
    tenantId: String(sub.tenant_id),
    category: courtesy?.category ?? discount?.lifetime_free_category ?? 'OTHER',
    reason: courtesy?.reason ?? discount?.lifetime_free_reason ?? null,
    appliedAt:
      courtesy?.applied_at ?? discount?.lifetime_free_applied_at ?? discount?.started_at ?? 0,
    appliedBy: courtesy?.applied_by ?? discount?.lifetime_free_applied_by ?? null,
    totalAmountCents: sub.total_amount_cents ?? 0,
    currency: sub.currency,
    billingCycle: sub.billing_cycle,
    livemode: sub.livemode ?? null,
  };
}

/**
 * Cortesias, por duas collection-group queries em `billing` (#679 F7):
 *
 * - a nova, `commercial_mode == 'COURTESY'` + `courtesy.applied_at` DESC;
 * - a legada, `discount.is_lifetime_free == true` + `discount.lifetime_free_applied_at`
 *   DESC — a cortesia que ainda vive de cupom até a E1 só aparece por ela.
 *
 * As duas se sobrepõem entre a E1 e a E3 (doc COURTESY que ainda tem o cupom);
 * a lista deduplica por tenant, e a primeira fonte vence. Quando a E3 limpar o
 * último `discount`, a query legada pode sair — junto com o índice dela.
 * Ambas exigem índice em `firestore.indexes.json`. Erro em qualquer uma é
 * erro da lista: uma lista pela metade seria lida como completa.
 *
 * Origem: sem issue — commit 4c08bb698, 2026-05-26 (Sprint 35 PR G2);
 * duas fontes desde a #679 F7.
 *
 * Usado por: `routes/PromotionsPage.tsx` e
 * `features/dashboard-finance/LifetimeFreeCard.tsx`.
 *
 * Arquitetura: collection group `billing`, autorizada pela rule
 * `match /{path=**}/billing/{billingDoc}` (só super-admin). Índices
 * COLLECTION_GROUP: `billing (commercial_mode ASC, courtesy.applied_at DESC)`
 * e `billing (discount.is_lifetime_free ASC, discount.lifetime_free_applied_at DESC)`.
 *
 * Dados: `tenants/{tid}/billing/subscription` (ver `subscribeBilling`); o
 * doc de outro id em `billing` (ex.: `usage`) não casa nos filtros.
 *
 * Dicas: o teto de 200 por fonte é sem paginação — a lista some calada
 * além disso.
 */
export function subscribeLifetimeFreeTenants(
  onResult: (r: LifetimeFreeListResult) => void,
): Unsubscribe {
  const ref = collectionGroup(firestore, 'billing');
  const sources = [
    query(
      ref,
      where('commercial_mode', '==', 'COURTESY'),
      orderBy('courtesy.applied_at', 'desc'),
      fsLimit(200),
    ),
    query(
      ref,
      where('discount.is_lifetime_free', '==', true),
      orderBy('discount.lifetime_free_applied_at', 'desc'),
      fsLimit(200),
    ),
  ];
  const latest: (LifetimeFreeTenantRow[] | null)[] = sources.map(() => null);
  let failed = false;
  /** Emite só quando as duas fontes responderam; deduplica por tenant (a primeira vence). */
  const emit = (): void => {
    if (failed || latest.some((rows) => rows === null)) return;
    const byTenant = new Map<string, LifetimeFreeTenantRow>();
    for (const rows of latest) {
      for (const row of rows ?? []) {
        if (!byTenant.has(row.tenantId)) byTenant.set(row.tenantId, row);
      }
    }
    const rows = [...byTenant.values()].sort((a, b) => b.appliedAt - a.appliedAt);
    onResult({ kind: 'READY', rows });
  };
  const unsubs = sources.map((q, i) =>
    onSnapshot(
      q,
      (snap) => {
        latest[i] = snap.docs
          .map((d) => lifetimeFreeRowFrom(d.data()))
          .filter((row): row is LifetimeFreeTenantRow => row !== null);
        emit();
      },
      (err) => {
        console.error(`${TAG}: courtesy CG query error`, err);
        failed = true;
        onResult({ kind: 'ERROR', error: err });
      },
    ),
  );
  return (): void => {
    for (const u of unsubs) u();
  };
}

// ---------- Cross-tenant invoices (PendingPage) ----------

/** Estado da lista cross-tenant de faturas. */
export type CrossTenantInvoicesResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; invoices: Invoice[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Collection-group query — exige índice composto em
 * `firestore.indexes.json` (status ASC + due_at ASC).
 *
 * Faturas `PENDING`/`OVERDUE` de todos os tenants, vencimento mais antigo
 * primeiro, até 200, sem as do Stripe sandbox.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 (Sprint 16, PendingPage).
 *
 * Usado por: `routes/PendingPage.tsx`.
 *
 * Arquitetura: rule `match /{path=**}/invoices/{invoiceId}` (super-admin);
 * índice COLLECTION_GROUP `invoices (status ASC, due_at ASC)`.
 *
 * Falhas conhecidas:
 *  - Fatura fora do schema é descartada sem log nem report (#926).
 */
export function subscribeCrossTenantInvoicesByStatus(
  status: 'PENDING' | 'OVERDUE',
  onResult: (r: CrossTenantInvoicesResult) => void,
): Unsubscribe {
  const ref = collectionGroup(firestore, 'invoices');
  const q = query(
    ref,
    where('status', '==', status),
    orderBy('due_at', 'asc'),
    fsLimit(200),
  );
  return onSnapshot(
    q,
    (snap) => {
      const invoices: Invoice[] = [];
      for (const d of snap.docs) {
        const parsed = InvoiceSchema.safeParse({ id: d.id, ...d.data() });
        // Faturas do Stripe sandbox não são cobrança real — fora da lista.
        // Filtro em memória de propósito: `where('livemode','==',true)` (e
        // `!= false`) descartaria também os docs legados sem o campo, que são
        // receita legítima. Ver `isTestModeRecord`.
        if (parsed.success && !isTestModeRecord(parsed.data)) {
          invoices.push(parsed.data);
        }
      }
      onResult({ kind: 'READY', invoices });
    },
    (err) => {
      console.error(`${TAG}: cross-tenant invoices ${status} error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

// ---------- Email logs ----------

// O formato da linha e o mapper moram em `./emailLogs` (#795, ADM-5); o
// re-export mantém o import de quem já consumia daqui.
export type { EmailLogEntry };

/** Estado da aba Logs de `/billing/emails`. */
export type EmailLogListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; entries: EmailLogEntry[] }
  | { kind: 'ERROR'; error: unknown };

// ---------- Sprint 5 PR H — Cross-tenant payments & refunds ----------

/** Estado da lista cross-tenant de pagamentos. */
export type CrossTenantPaymentsResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; payments: Payment[] }
  | { kind: 'ERROR'; error: unknown };

/** Filtros (mutuamente exclusivos) da lista de transações. */
export type CrossTenantPaymentsFilters = {
  /** `null` = qualquer status. */
  status: PaymentStatus | null;
  /** `null` = qualquer provider. */
  provider: PaymentProvider | null;
  /** `null` = qualquer método. */
  methodType: PaymentMethodType | null;
};

/** Teto das listas financeiras cross-tenant e de refunds por tenant. */
export const FINANCE_LIST_LIMIT = 100;

/**
 * Listagem cross-tenant de pagamentos. Combina filtros opcionais +
 * `orderBy('created_at', 'desc')`. Quando há filtro por igualdade,
 * usa o índice composto `payments` (status|provider|payment_method_type
 * × created_at). Sem filtro, depende do single-field index com escopo
 * `COLLECTION_GROUP` em `created_at` (declarado em fieldOverrides).
 *
 * Filtros são mutuamente exclusivos no MVP — combinar dois exigiria
 * índice por par e a UI já permite drill-down por tenant para refinar.
 *
 * Origem: sem issue — commit 973be8d9c, 2026-05-05 (Sprint 5 PR H, painel
 * financeiro).
 *
 * Contrato: precedência `status` > `provider` > `methodType` — se vierem
 * dois, só o primeiro vale. Pagamentos do Stripe sandbox ficam fora.
 *
 * Usado por: `routes/TransactionsPage.tsx`.
 *
 * Arquitetura: rule `match /{path=**}/payments/{paymentId}` (super-admin).
 * Índices COLLECTION_GROUP: `payments (status|provider|payment_method_type
 * ASC, created_at DESC)`; sem filtro, o `fieldOverrides` de `created_at`.
 *
 * Dados: `tenants/{tid}/payments/{id}` — `PaymentSchema`.
 *
 * Falhas conhecidas:
 *  - Doc fora do schema some da lista só com `console.warn`, sem report na
 *    telemetria (#926).
 */
export function subscribeCrossTenantPayments(
  filters: CrossTenantPaymentsFilters,
  onResult: (r: CrossTenantPaymentsResult) => void,
): Unsubscribe {
  const ref = collectionGroup(firestore, 'payments');
  const constraints = [];
  if (filters.status !== null) {
    constraints.push(where('status', '==', filters.status));
  } else if (filters.provider !== null) {
    constraints.push(where('provider', '==', filters.provider));
  } else if (filters.methodType !== null) {
    constraints.push(where('payment_method_type', '==', filters.methodType));
  }
  const q = query(
    ref,
    ...constraints,
    orderBy('created_at', 'desc'),
    fsLimit(FINANCE_LIST_LIMIT),
  );
  return onSnapshot(
    q,
    (snap) => {
      const payments: Payment[] = [];
      for (const d of snap.docs) {
        const parsed = PaymentSchema.safeParse({ id: d.id, ...d.data() });
        if (parsed.success) {
          // Pagamento do Stripe sandbox não é dinheiro — não entra no
          // "Total pago"/"Nº de cobranças". Filtro em memória: um
          // `where('livemode','==',true)` sumiria com os pagamentos legados
          // (sem o campo), que são reais. Ver `isTestModeRecord`.
          if (!isTestModeRecord(parsed.data)) payments.push(parsed.data);
        } else {
          console.warn(`${TAG}: cross-tenant payment ${d.id} ignorado`, parsed.error.issues);
        }
      }
      onResult({ kind: 'READY', payments });
    },
    (err) => {
      console.error(`${TAG}: cross-tenant payments error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

/** Estado de uma lista de refunds. */
export type RefundListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; refunds: Refund[] }
  | { kind: 'ERROR'; error: unknown };

/** Filtros (mutuamente exclusivos) da lista de refunds. */
export type CrossTenantRefundsFilters = {
  status: RefundStatus | null;
  reason: RefundReason | null;
};

/**
 * Listagem cross-tenant de refunds. Mesmo padrão dos payments — filtros
 * por igualdade + ordenação por `requested_at`. Quando ambos os filtros
 * estão `null`, depende do single-field index `requested_at` (escopo
 * `COLLECTION_GROUP`).
 *
 * Origem: sem issue — commit 973be8d9c, 2026-05-05 (Sprint 5 PR H).
 *
 * Contrato: precedência `status` > `reason`.
 *
 * Usado por: `routes/RefundsPage.tsx`.
 *
 * Arquitetura: rule `match /{path=**}/refunds/{refundId}`. Índices
 * COLLECTION_GROUP: `refunds (status ASC, requested_at DESC)` e
 * `refunds (reason ASC, requested_at DESC)`.
 *
 * Dados: `tenants/{tid}/refunds/{id}` — `RefundSchema`.
 *
 * Falhas conhecidas:
 *  - Doc fora do schema some da lista só com `console.warn`, sem report na
 *    telemetria (#926).
 */
export function subscribeCrossTenantRefunds(
  filters: CrossTenantRefundsFilters,
  onResult: (r: RefundListResult) => void,
): Unsubscribe {
  const ref = collectionGroup(firestore, 'refunds');
  const constraints = [];
  if (filters.status !== null) {
    constraints.push(where('status', '==', filters.status));
  } else if (filters.reason !== null) {
    constraints.push(where('reason', '==', filters.reason));
  }
  const q = query(
    ref,
    ...constraints,
    orderBy('requested_at', 'desc'),
    fsLimit(FINANCE_LIST_LIMIT),
  );
  return onSnapshot(
    q,
    (snap) => {
      const refunds: Refund[] = [];
      for (const d of snap.docs) {
        const parsed = RefundSchema.safeParse({ id: d.id, ...d.data() });
        if (parsed.success) {
          refunds.push(parsed.data);
        } else {
          console.warn(`${TAG}: refund ${d.id} ignorado`, parsed.error.issues);
        }
      }
      onResult({ kind: 'READY', refunds });
    },
    (err) => {
      console.error(`${TAG}: cross-tenant refunds error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

/**
 * Refunds de um tenant específico (drill-down em /admin/tenants/:tid).
 * A query só ordena por `requested_at desc` (sem filtro), então usa o índice
 * de campo único (`fieldOverrides` de `requested_at`, escopo COLLECTION) — o
 * composto `refunds (status, requested_at)` não é usado aqui. Comentário
 * corrigido em 2026-10-10 (#926): dizia que usava o composto.
 *
 * Origem: sem issue — commit 973be8d9c, 2026-05-05 (Sprint 5 PR H).
 *
 * Usado por: `features/finance/TenantFinanceTab.tsx`.
 *
 * Falhas conhecidas:
 *  - Refund fora do schema é descartado sem log nem report (#926).
 */
export function subscribeTenantRefunds(
  tenantId: string,
  onResult: (r: RefundListResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'tenants', tenantId, 'refunds');
  const q = query(ref, orderBy('requested_at', 'desc'), fsLimit(FINANCE_LIST_LIMIT));
  return onSnapshot(
    q,
    (snap) => {
      const refunds: Refund[] = [];
      for (const d of snap.docs) {
        const parsed = RefundSchema.safeParse({ id: d.id, ...d.data() });
        if (parsed.success) refunds.push(parsed.data);
      }
      onResult({ kind: 'READY', refunds });
    },
    (err) => {
      console.error(`${TAG}: refunds error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

/**
 * Listener da aba Logs de `/billing/emails`: os 100 e-mails mais recentes de
 * `/emailLogs`, de todas as empresas ou de uma só.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 (rotas /billing/*, Sprint 16).
 *
 * Contrato: `tenantId` `null` = sem filtro (`orderBy createdAt desc`, índice
 * automático); string = `where('tenantId', '==') + orderBy('createdAt',
 * 'desc')`, que exige o índice composto `emailLogs (tenantId ASC, createdAt
 * DESC)`. Cada doc passa por `toEmailLogEntry` (Zod); fora do schema é
 * descartado e reportado UMA vez por inscrição
 * (`admin_email_logs_doc_dropped`). Erro do listener → `ERROR` +
 * `reportNonFatal('admin_email_logs_failed')`.
 *
 * Usado por: `routes/BillingEmailsPage.tsx` (web `apps/admin`, super-admin);
 * as linhas vão para `features/billing/EmailLogsTable.tsx`.
 *
 * Dados: formato do doc e exemplo em `./emailLogs.ts`.
 *
 * Falhas conhecidas:
 *  - 2026-05-05 → 2026-10-03 (#795, ADM-5): filtrava `custom_args.tenantId`,
 *    campo que o doc nunca teve, com `orderBy('createdAt')` sem índice
 *    composto — `failed-precondition`, a tela mostrava só "Falha ao carregar
 *    logs" e nada ia para a telemetria.
 *
 * Histórico:
 *  - 2026-10-03 14:12 +0200 — (#795, ADM-5) filtro pelo `tenantId` do topo +
 *    índice; mapper com Zod; erro e descarte reportados ao PostHog.
 */
export function subscribeEmailLogsByTenant(
  tenantId: string | null,
  onResult: (r: EmailLogListResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'emailLogs');
  // Bug corrigido em 2026-10-03 14:12 +0200 (#795, ADM-5): o filtro era
  // `where('custom_args.tenantId', ...)` — `custom_args` só vai ao SendGrid,
  // o doc guarda `tenantId` no topo (`sendInternalEmail.ts`) — e a query não
  // tinha índice composto, então caía em `failed-precondition`. Não remover o
  // índice `emailLogs (tenantId, createdAt DESC)` de `firestore.indexes.json`.
  const q =
    tenantId === null
      ? query(ref, orderBy('createdAt', 'desc'), fsLimit(100))
      : query(
          ref,
          where('tenantId', '==', tenantId),
          orderBy('createdAt', 'desc'),
          fsLimit(100),
        );
  // Um report por inscrição: o listener dispara de novo a cada evento do
  // webhook (o doc pai é mesclado), e o mesmo doc ruim viraria uma enxurrada.
  let dropsReported = false;
  return onSnapshot(
    q,
    (snap) => {
      const entries: EmailLogEntry[] = [];
      const dropped: string[] = [];
      for (const d of snap.docs) {
        const entry = toEmailLogEntry(d.id, d.data());
        if (entry) entries.push(entry);
        else dropped.push(d.id);
      }
      if (dropped.length > 0 && !dropsReported) {
        dropsReported = true;
        reportNonFatal('admin_email_logs_doc_dropped', {
          count: dropped.length,
          sample: dropped.slice(0, 5),
        });
      }
      onResult({ kind: 'READY', entries });
    },
    (err) => {
      console.error(`${TAG}: emailLogs error`, err);
      reportNonFatal('admin_email_logs_failed', {
        filtered: tenantId !== null,
        ...serializeError(err),
      });
      onResult({ kind: 'ERROR', error: err });
    },
  );
}
